import React from "react";
import styled from "styled-components";
import {
  Elevation,
  FontSize,
  FontWeight,
  GlossyThemeProvider,
  Radius,
  Spacing,
  background,
  borderRadius,
  content,
  cover,
  form,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Link from "@ui-kit/utils/link";
import { tr } from "@radarly/i18n";
import {
  Benchmark,
  BrandsBenchmark,
  BrandsFocus,
  BrandsOverview,
  Campaign,
  Consumer,
  CustomerExperience,
  Reputation,
} from "./illustrations";

export const ILLUSTRATIONS = {
  benchmark: Benchmark,
  brands_benchmark: BrandsBenchmark,
  brands_focus: BrandsFocus,
  brands_overview: BrandsOverview,
  campaign: Campaign,
  consumer: Consumer,
  customer_experience: CustomerExperience,
  reputation: Reputation,
};

export interface IProps {
  viz: string;
  title: string;
  description: string;
  url: string;
  active?: boolean;
  locale?: Locale;
  onClick?: () => void;
}

const Card = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 160px;
  border-radius: ${Radius.Medium};
  color: ${content};
  background-color: ${(props) =>
    transparentize(0.85, content(props, "secondary"))};
  overflow: hidden;
`;

const Viz = styled.div`
  width: 110px;
  background-color: ${background};
  height: 136px;
  ${borderRadius("top", Radius.Medium)};
`;

const Title = styled.div`
  color: ${content};
  font-weight: ${FontWeight.Bold};
  margin: ${Spacing.Medium} 0 ${Spacing.ExtraSmall};
  word-break: break-word;
  > * {
    text-align: center;
  }
`;

const Description = styled.div`
  color: ${(props) => content(props, "secondary")};
  line-height: 1.2;
`;

const StyledLink = styled(Link)<{ $active: boolean }>`
  position: relative;
  display: block;
  width: 204px;
  height: 300px;
  padding: ${Spacing.Medium};
  background-color: ${(props) =>
    transparentize(props.$active ? 0 : 0.5, background(props))};
  border-radius: ${Radius.Medium};
  box-shadow: ${Elevation.Elevation_1};
  pointer-events: ${(props) => !props.$active && "none"};
  :hover {
    text-decoration: none;
  }
  :after {
    pointer-events: none;
    content: "";
    ${cover()};
    border-radius: inherit;
  }
  :hover:after {
    background-color: ${(props) => transparentize(0.8, form(props, "primary"))};
  }

  ${Card} {
    opacity: ${(props) => !props.$active && 0.5};
  }

  ${Description} {
    opacity: ${(props) => !props.$active && 0.5};
  }

  ${Title} {
    opacity: ${(props) => !props.$active && 0.5};
  }
`;

const AvailableSoon = styled.div`
  position: absolute;
  top: 64px;
  right: 0;
  left: 0;
  color: ${content};
  text-align: center;
  padding: ${Spacing.ExtraSmall};
  font-size: ${FontSize.Large};
  font-weight: ${FontWeight.Bold};
  line-height: normal;
  /* stylelint-disable-next-line color-no-hex */
  background-color: #f8f8fa;
`;

const PanelsCardLibraryModelCard = ({
  viz,
  title,
  description,
  url,
  active = true,
  onClick,
  locale = Locale.English,
}: IProps) => (
  <GlossyThemeProvider name="surface">
    <StyledLink url={url} onClick={onClick} $active={active}>
      <Card>
        <Viz>
          {viz &&
            React.createElement(
              ILLUSTRATIONS[viz] || ILLUSTRATIONS["benchmark"]
            )}
        </Viz>
      </Card>
      <Title>{title}</Title>
      <Description>{description}</Description>
      {!active && (
        <AvailableSoon
          dangerouslySetInnerHTML={{
            __html: tr(locale, "CUSTOM_PAGES::EDITOR::AVAILABLE_SOON"),
          }}
        />
      )}
    </StyledLink>
  </GlossyThemeProvider>
);

export default PanelsCardLibraryModelCard;
